import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { RotateCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtMoney } from "@/lib/format";

const BADGE = {
    retrying: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
    flagged: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
    recovered: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    resolved: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
};

export default function PaymentRecovery() {
    const [cases, setCases] = useState([]);
    const load = () => api.get("/payment-recovery").then(({ data }) => setCases(data));
    useEffect(() => { load(); }, []);
    const run = async (fn, msg) => {
        try { const r = await fn(); toast.success(msg(r?.data)); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const flagged = cases.filter((c) => c.status === "flagged").length;
    const retrying = cases.filter((c) => c.status === "retrying").length;

    return (
        <div data-testid="payment-recovery-page">
            <PageHeader title="Failed Payment Recovery"
                        subtitle="Failed card & ACH payments are retried 3 times over a week (days 3, 5, 7) with an email each time, then flagged for you" />
            <div className="px-8 py-6 space-y-4">
                <div className="flex gap-3 text-sm items-center">
                    <OpenCase onDone={load} />
                    <span className="px-3 py-1.5 rounded border" data-testid="pr-retrying-count"><b>{retrying}</b> retrying</span>
                    <span className="px-3 py-1.5 rounded border border-red-300 text-red-700" data-testid="pr-flagged-count">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /><b>{flagged}</b> need you
                    </span>
                </div>
                <Card><CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pr-table">
                        <thead className="bg-secondary/40 text-xs text-muted-foreground">
                            <tr>{["Customer", "Amount", "Source", "Status", "Attempts", "Next retry", ""].map((h) => (
                                <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>))}</tr>
                        </thead>
                        <tbody>
                            {cases.map((c) => <CaseRow key={c.id} c={c} run={run} />)}
                            {!cases.length && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="pr-empty">No failed payments</td></tr>}
                        </tbody>
                    </table>
                </CardContent></Card>
            </div>
        </div>
    );
}

function OpenCase({ onDone }) {
    const [customers, setCustomers] = useState([]);
    const [cid, setCid] = useState("");
    const [amount, setAmount] = useState("");
    const [ach, setAch] = useState(false);
    useEffect(() => { api.get("/customers", { params: { limit: 500 } }).then(({ data }) => setCustomers(data)); }, []);
    const open = async () => {
        if (!cid) return toast.error("Pick a customer");
        try {
            await api.post("/payment-recovery", { customer_id: cid, amount: parseFloat(amount) || undefined, ach_return: ach });
            toast.success("Recovery started: retries on days 3, 5 and 7"); setCid(""); setAmount(""); setAch(false); onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="flex items-center gap-2 mr-4" data-testid="pr-open-form">
            <Select value={cid} onValueChange={setCid}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="pr-open-customer"><SelectValue placeholder="Record a failed payment…" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`pr-open-customer-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-8 w-24 text-xs" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pr-open-amount" />
            <label className="text-xs flex items-center gap-1"><Checkbox checked={ach} onCheckedChange={(v) => setAch(!!v)} data-testid="pr-open-ach" />ACH return</label>
            <Button size="sm" variant="outline" onClick={open} data-testid="pr-open-btn">Start recovery</Button>
        </div>
    );
}

function CaseRow({ c, run }) {
    const [note, setNote] = useState("");
    const active = ["retrying", "flagged"].includes(c.status);
    const last = c.attempts[c.attempts.length - 1];
    return (
        <tr className="border-t border-border align-top" data-testid={`pr-row-${c.id}`}>
            <td className="px-4 py-3"><div className="font-medium">{c.customer?.name || "—"}</div>
                <div className="text-[11px] text-muted-foreground">{c.customer?.account_number}</div></td>
            <td className="px-4 py-3 tabular">{fmtMoney(c.amount)}</td>
            <td className="px-4 py-3 text-xs">{c.source}{c.ach_return && <span className="ml-1 text-red-600 font-semibold">ACH return</span>}</td>
            <td className="px-4 py-3"><span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${BADGE[c.status]}`}>{c.status}</span></td>
            <td className="px-4 py-3 text-xs">{c.attempts.length}<div className="text-muted-foreground truncate max-w-[200px]">{last?.error}</div></td>
            <td className="px-4 py-3 text-xs">{c.next_retry_at ? c.next_retry_at.slice(0, 10) : "—"}</td>
            <td className="px-4 py-3">
                {active && (
                    <div className="flex flex-wrap gap-2 items-center">
                        <Button size="sm" variant="outline" data-testid={`pr-retry-${c.id}`}
                                onClick={() => run(() => api.post(`/payment-recovery/${c.id}/retry`), (d) => d?.status === "retrying" ? `Attempt ${d.attempt} failed. Next retry ${d.next_retry_at?.slice(0, 10)}`
                                    : d?.status === "flagged" ? "All retries failed. Flagged for you" : "Payment recovered")}>
                            <RotateCw className="w-3.5 h-3.5 mr-1" />Retry now
                        </Button>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="h-8 w-28 text-xs" />
                        <Button size="sm" variant="ghost" data-testid={`pr-resolve-${c.id}`}
                                onClick={() => run(() => api.post(`/payment-recovery/${c.id}/resolve`, { note }), () => "Marked resolved")}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Resolve
                        </Button>
                    </div>
                )}
            </td>
        </tr>
    );
}
